import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { clearCopyFeedback } from './useCopyToClipboard';
import { COPY_ACTIVE_ATTR, STICKY_TABLE_COPY_FRONT_ATTR } from '../utils/tableCopySx';

/**
 * Sticky-header tables: lift the first row's copy bubble above the header at scroll
 * top so it can overflow upward. As soon as the user scrolls (rows move), copy
 * feedback is cleared and the header covers any remaining bubble. If the table
 * cannot scroll, the bubble stays in front.
 */
export function useStickyTableCopyFront(
    scrollRef: RefObject<HTMLElement | null>,
    /** Change when table content/size may affect scrollability (e.g. load or filter). */
    syncKey?: string | number,
) {
    const [copyInFront, setCopyInFront] = useState(true);
    const copyInFrontRef = useRef(copyInFront);
    copyInFrontRef.current = copyInFront;

    const sync = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const canScroll = el.scrollHeight > el.clientHeight + 1;
        const atTop = el.scrollTop === 0;
        const nextInFront = !canScroll || atTop;

        if (copyInFrontRef.current && !nextInFront) {
            // Only clear feedback when the active copy lives in THIS scroll container,
            // so scrolling a sibling sticky table can't dismiss an unrelated bubble.
            if (el.querySelector(`[${COPY_ACTIVE_ATTR}]`)) {
                clearCopyFeedback();
            }
        }

        copyInFrontRef.current = nextInFront;
        setCopyInFront(nextInFront);
    }, [scrollRef]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        sync();
        el.addEventListener('scroll', sync, { passive: true });
        const resizeObserver = new ResizeObserver(sync);
        resizeObserver.observe(el);

        return () => {
            el.removeEventListener('scroll', sync);
            resizeObserver.disconnect();
        };
    }, [scrollRef, sync, syncKey]);

    const scrollContainerProps = {
        [STICKY_TABLE_COPY_FRONT_ATTR]: copyInFront ? 'true' : undefined,
    } as const;

    return { copyInFront, scrollContainerProps };
}
